import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useCurrency } from "../../lib/contexts";
import { todayIso } from "../../lib/permissions";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

export default function SampleSentDialog({ prompt, onClose, onSaved }) {
  const [sampleDate, setSampleDate] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [saving, setSaving] = useState(false);
  const { format } = useCurrency();
  useEffect(() => {
    if (prompt?.deal) {
      // Sensible defaults: sample date = today, reminder = today + 7 days
      const today = todayIso();
      const plus7 = new Date();
      plus7.setDate(plus7.getDate() + 7);
      const remDefault = plus7.toISOString().slice(0, 10);
      setSampleDate(prompt.deal.sample_sent_date ? prompt.deal.sample_sent_date.slice(0, 10) : today);
      setReminderDate(remDefault);
    }
  }, [prompt]);
  if (!prompt) return null;
  const deal = prompt.deal;
  const reminderPast = !!reminderDate && reminderDate < todayIso();
  const canSubmit = !!sampleDate && !!reminderDate && !reminderPast;
  const submit = async (e) => {
    e.preventDefault();
    if (!sampleDate) { toast.error("Please pick the Sample Sent Date"); return; }
    if (!reminderDate) { toast.error("Please pick a Next Reminder Date"); return; }
    if (reminderPast) { toast.error("Next Reminder Date cannot be in the past"); return; }
    setSaving(true);
    try {
      await api.patch(`/deals/${deal.id}/stage`, {
        stage: "Sample Sent",
        sample_sent_date: sampleDate,
        next_reminder_date: reminderDate,
      });
      toast.success("Moved to Sample Sent");
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <Dialog open={!!prompt} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-blue-700">Move to Sample Sent</DialogTitle>
        </DialogHeader>
        <div className="border border-border rounded-md bg-secondary/40 p-3 space-y-1 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">{deal.product_name || "—"}</span>
            {deal.product_code && (
              <span className="text-xs font-mono bg-neutral-200 px-1.5 py-0.5 rounded">{deal.product_code}</span>
            )}
          </div>
          <div className="text-muted-foreground">{deal.contact_name}</div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1 flex-wrap">
            <span>Owner: <span className="text-neutral-800 font-medium">{deal.created_by_name || "—"}</span></span>
            <span>Estimated: <span className="text-neutral-800 font-medium">{format(deal.estimated_value)}</span></span>
            <span>From: <span className="text-neutral-800 font-medium">{deal.stage}</span> → <span className="text-blue-700 font-semibold">Sample Sent</span></span>
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-sm font-semibold">Sample Sent Date <span className="text-destructive">*</span></Label>
            <p className="text-[10px] text-muted-foreground mb-1">Historical date — when did we actually dispatch the sample?</p>
            <Input type="date" data-testid="sample-sent-date" value={sampleDate}
              onChange={(e) => setSampleDate(e.target.value)} className="border-2 border-blue-200 focus-visible:border-blue-500" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Next Reminder Date <span className="text-destructive">*</span></Label>
            <p className="text-[10px] text-muted-foreground mb-1">Cannot be in the past — this becomes the deal's active follow-up.</p>
            <Input type="date" data-testid="sample-reminder-date" value={reminderDate}
              min={todayIso()}
              onChange={(e) => setReminderDate(e.target.value)}
              className={`border-2 focus-visible:border-blue-500 ${reminderPast ? "border-destructive" : "border-blue-200"}`} />
            {reminderPast && <div className="text-[10px] text-destructive mt-1">Please pick today or a future date.</div>}
          </div>
          <div className="col-span-2 flex justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="sample-submit" type="submit" disabled={saving || !canSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              {saving ? "Saving…" : "Save & Move to Sample Sent"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
